import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { AllStatusCounts } from '../../interfaces/status-counts/all-status-counts';
import { AppointmentCount } from '../../interfaces/status-counts/appointment-counts';
import { CclCounts } from '../../interfaces/status-counts/ccl-counts';
import { CustomerVisitCounts } from '../../interfaces/status-counts/customer-visit-counts';
import { LeadCounts } from '../../interfaces/status-counts/lead-counts';
import { PermitCounts } from '../../interfaces/status-counts/permit-counts';
import { ProductionCounts } from '../../interfaces/status-counts/production-counts';
import { ProductionManagementCounts } from '../../interfaces/status-counts/production-management-counts';
import { ProductionSchedulingCounts } from '../../interfaces/status-counts/production-scheduling-counts';
import { ProposalCounts } from '../../interfaces/status-counts/proposal-counts';
import { SalesManagementCounts } from '../../interfaces/status-counts/sales-management-counts';
import { ServiceCounts } from '../../interfaces/status-counts/service-counts';
import { ServiceManagementCounts } from '../../interfaces/status-counts/service-management-counts';
import { UtilityCounts } from '../../interfaces/status-counts/utility-counts';
import { DataService } from '../data/data.service';
import { IdbService } from '../idb/idb.service';
import { RoleService } from '../role/role.service';
import { AccountingCounts } from '../../interfaces/status-counts/accounting-counts';

@Injectable({
  providedIn: 'root',
})
export class CountsService {
  // Counts to display observer
  private countsToDisplaySubject = new BehaviorSubject<string[]>([]);
  currentCountsToDisplay = this.countsToDisplaySubject.asObservable();

  constructor(private role: RoleService, private idb: IdbService) {}

  buildCountsToDisplay(path: string): string[] {
    let countsToDisplay: string[] = [];
    switch (path) {
      case path.match('/dashboard')?.input:
        countsToDisplay = [
          'leadCounts',
          'appointmentCounts',
          'customerVisitCounts',
          // 'proposalCounts',
          'salesManagementCounts',
          'productionSchedulingCounts',
          // 'productionCounts',
          'productionManagementCounts',
          'serviceCounts',
          'serviceManagementCounts',
          'permitCounts',
          'utilitiesCount',
          'cclCounts',
          'accountingCounts',
        ];
        break;
      case path.match('/admin')?.input:
        countsToDisplay = [
          'leadCounts',
          'appointmentCounts',
          'customerVisitCounts',
          // 'proposalCounts',
          'salesManagementCounts',
          'productionSchedulingCounts',
          // 'productionCounts',
          'productionManagementCounts',
          'serviceCounts',
          'serviceManagementCounts',
          'permitCounts',
          'utilitiesCount',
          'cclCounts',
          'accountingCounts',
        ];
        break;
      case path.match('/reports')?.input:
        countsToDisplay = [
          'leadCounts',
          'appointmentCounts',
          'customerVisitCounts',
          // 'proposalCounts',
          'salesManagementCounts',
          'productionSchedulingCounts',
          // 'productionCounts',
          'productionManagementCounts',
          'serviceCounts',
          'serviceManagementCounts',
          'permitCounts',
          'utilitiesCount',
          'cclCounts',
          'accountingCounts',
        ];
        break;
      case path.match('/followUps')?.input:
        countsToDisplay = [
          'leadCounts',
          'appointmentCounts',
          'customerVisitCounts',
          // 'proposalCounts',
          'salesManagementCounts',
          'productionSchedulingCounts',
          // 'productionCounts',
          'productionManagementCounts',
          'serviceCounts',
          'serviceManagementCounts',
          'permitCounts',
          'utilitiesCount',
          'cclCounts',
          'accountingCounts',
        ];
        break;
      case path.match('/calendar')?.input:
        countsToDisplay = [
          'appointmentCount',
          'customerVisitCount',
          // 'proposalCount',
        ];
        break;
      case path.match('/counts/list/leadCounts')?.input:
        countsToDisplay = ['leadCounts'];
        break;
      case path.match('/counts/list/appointmentCounts')?.input:
        countsToDisplay = ['appointmentCounts'];
        break;
      case path.match('/counts/list/cclCounts')?.input:
        countsToDisplay = ['cclCounts'];
        break;
      case path.match('/counts/list/customerVisitCounts')?.input:
        countsToDisplay = ['customerVisitCounts'];
        break;
      case path.match('/counts/list/permitCounts')?.input:
        countsToDisplay = ['permitCounts'];
        break;
      case path.match('/counts/list/utilityCounts')?.input:
        countsToDisplay = ['utilitiesCount'];
        break;
      // case path.match('/counts/list/productionCounts')?.input:
      //   countsToDisplay = ['productionCounts'];
      //   break;
      case path.match('/counts/list/productionManagementCounts')?.input:
        countsToDisplay = ['productionManagementCounts'];
        break;
      case path.match('/counts/list/productionSchedulingCounts')?.input:
        countsToDisplay = ['productionSchedulingCounts'];
        break;
      // case path.match('/counts/list/proposalCounts')?.input:
      //   countsToDisplay = ['proposalCounts'];
      //   break;
      case path.match('/counts/list/salesManagementCounts')?.input:
        countsToDisplay = ['salesManagementCounts'];
        break;
      case path.match('/counts/list/serviceCounts')?.input:
        countsToDisplay = ['serviceCounts'];
        break;
      case path.match('/counts/list/serviceManagementCounts')?.input:
        countsToDisplay = ['serviceManagementCounts'];
        break;
      case path.match('/counts/list/accountingCounts')?.input:
        countsToDisplay = ['accountingCounts'];
        break;
    }
    const filteredCounts = this.filterCountsBasedOnRole(countsToDisplay);
    return filteredCounts;
  }

  filterCountsBasedOnRole(countsToDisplay: string[]): string[] {
    const role = this.role.getCurrentUserRole();
    switch (role) {
      case 'Admin':
        // All counts
        break;
      case 'Super Admin':
        // All counts
        break;
      case 'Sales Manager':
        countsToDisplay = countsToDisplay.filter(
          (count) =>
            count === 'salesManagementCounts' ||
            // count === 'proposalCounts' ||
            count === 'customerVisitCounts' ||
            count === 'productionSchedulingCounts'
        );
        break;
      case 'Warehouse':
        countsToDisplay = countsToDisplay.filter(
          (count) =>
            count === 'productionSchedulingCounts' || count === 'serviceCounts'
        );
        break;
      case 'Office Staff':
        countsToDisplay = countsToDisplay.filter(
          (count) =>
            count === 'leadCounts' ||
            count === 'appointmentCounts' ||
            count === 'productionSchedulingCounts' ||
            count === 'permitCounts' ||
            count === 'utilitiesCount' ||
            count === 'serviceCounts' ||
            count === 'cclCounts'
        );
        break;
      case 'Customer Service Admin':
        countsToDisplay = countsToDisplay.filter(
          (count) =>
            count === 'leadCounts' ||
            count === 'appointmentCounts' ||
            count === 'productionSchedulingCounts' ||
            count === 'permitCounts' ||
            count === 'utilitiesCount' ||
            count === 'serviceCounts' ||
            count === 'cclCounts'
        );
        break;
      case 'Service Backup':
        countsToDisplay = countsToDisplay.filter(
          (count) =>
            count === 'leadCounts' ||
            count === 'appointmentCounts' ||
            count === 'productionSchedulingCounts' ||
            count === 'permitCounts' ||
            count === 'utilitiesCount' ||
            count === 'serviceCounts'
        );
        break;
      case 'Crew':
        // No counts
        countsToDisplay = [];
        break;
      case 'Customer Care Liaison':
        countsToDisplay = countsToDisplay.filter(
          (count) =>
            count === 'leadCounts' ||
            count === 'appointmentCounts' ||
            count === 'productionSchedulingCounts' ||
            count === 'cclCounts'
        );
        break;
      case 'Foreman':
        // No counts
        countsToDisplay = [];
        break;
      case 'Estimator':
        countsToDisplay = countsToDisplay.filter(
          (count) =>
            // count === 'proposalCounts' ||
            count === 'customerVisitCounts'
        );
        break;
      case 'Superintendent':
        countsToDisplay = countsToDisplay.filter(
          (count) =>
            count === 'productionSchedulingCounts' ||
            count === 'productionManagementCounts'
        );
        break;
      case 'Service Tech':
        countsToDisplay = [];
        // countsToDisplay = countsToDisplay.filter(
        //   (count) => count === 'serviceCounts'
        // );
        break;
      case 'Follow up Sales':
        countsToDisplay = countsToDisplay.filter(
          (count) =>
            // count === 'proposalCounts' ||
            count === 'customerVisitCounts'
        );
        break;
      case 'Call Center':
        countsToDisplay = countsToDisplay.filter(
          (count) =>
            count === 'leadCounts' ||
            count === 'appointmentCounts' ||
            count === 'cclCounts'
          // count === 'customerVisitCounts'
        );
        break;
      case 'Call Center Admin':
        countsToDisplay = countsToDisplay.filter(
          (count) =>
            count === 'leadCounts' ||
            count === 'appointmentCounts' ||
            count === 'cclCounts'
          // count === 'customerVisitCounts'
        );
        break;
      case 'Finance':
        countsToDisplay = countsToDisplay.filter(
          (count) =>
            count === 'productionSchedulingCounts' ||
            count === 'productionManagementCounts' ||
            count === 'serviceManagementCounts' ||
            count === 'accountingCounts'
        );
        break;
      case 'Production Manager':
        countsToDisplay = countsToDisplay.filter(
          (count) =>
            count === 'productionSchedulingCounts' ||
            count === 'productionManagementCounts'
        );
        break;
      case 'Service Manager':
        countsToDisplay = countsToDisplay.filter(
          (count) =>
            count === 'serviceCounts' || count === 'serviceManagementCounts'
        );
        break;
      case 'Marketing':
        // No counts
        countsToDisplay = [];
        break;
      case 'Production Scheduling':
        countsToDisplay = countsToDisplay.filter(
          (count) =>
            count === 'leadCounts' ||
            count === 'appointmentCounts' ||
            count === 'productionSchedulingCounts' ||
            count === 'permitCounts' ||
            count === 'utilitiesCount' ||
            count === 'serviceCounts' ||
            count === 'cclCounts'
        );
        break;
      case 'Service Scheduling':
        countsToDisplay = countsToDisplay.filter(
          (count) =>
            count === 'leadCounts' ||
            count === 'appointmentCounts' ||
            count === 'productionSchedulingCounts' ||
            count === 'permitCounts' ||
            count === 'utilitiesCount' ||
            count === 'serviceCounts' ||
            count === 'cclCounts'
        );
        break;
    }
    return countsToDisplay;
  }

  getOfflineCounts(roleName: string): Observable<AllStatusCounts> {
    const emptyCounts: AllStatusCounts = {
      appointmentCount: <AppointmentCount>{},
      cclCount: <CclCounts>{},
      customerVisitCount: <CustomerVisitCounts>{},
      leadCount: <LeadCounts>{},
      utilitiesCount: <UtilityCounts>{},
      permitCount: <PermitCounts>{},
      productionCount: <ProductionCounts>{},
      productionManagementCount: <ProductionManagementCounts>{},
      productionSchedulingCount: <ProductionSchedulingCounts>{},
      proposalCount: <ProposalCounts>{},
      salesManagementCount: <SalesManagementCounts>{},
      serviceCount: <ServiceCounts>{},
      serviceManagementCount: <ServiceManagementCounts>{},
      accountingCount: <AccountingCounts>{},
    };

    switch (roleName) {
      case 'Admin':
        break;
      case 'Estimator':
        emptyCounts.customerVisitCount = {
          total: 0,
          customerVisitsNeedingDisposition: 0,
          pendingCustomerVisits: 0,
          availableForInsideSales: 0,
          pendingDeadAppointments: 0,
          pendingChangeOrders: 0,
          hotQuotes: 0,
        };
        this.idb.getAllCustomerVisits().subscribe({
          next: (customerVisits) => {
            customerVisits.forEach((customerVisit) => {
              // Needs Dispo
              if (customerVisit.status === 1 || customerVisit.status === 3) {
                emptyCounts.customerVisitCount
                  .customerVisitsNeedingDisposition++;
                emptyCounts.customerVisitCount.total++;
              }
              // Pending
              if (customerVisit.status === 6) {
                emptyCounts.customerVisitCount.pendingCustomerVisits++;
                emptyCounts.customerVisitCount.total++;
              }
              // Hot Quote
              if (customerVisit.status === 8) {
                emptyCounts.customerVisitCount.hotQuotes++;
                emptyCounts.customerVisitCount.total++;
              }
            });
          },
        });
        break;
      case 'Foreman':
        break;
      case 'Service Tech':
        break;
    }

    return of(emptyCounts);
  }

  // Modify array after it's built to filter it by role
  // filterCountsToDisplay(countsToDisplay: string[]): string[] {
}
